"use strict";
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || function (mod) {
    if (mod && mod.__esModule) return mod;
    var result = {};
    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
    __setModuleDefault(result, mod);
    return result;
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.GameBoard = GameBoard;
const React = __importStar(require("react"));
const react_jss_1 = require("react-jss");
const board_1 = require("~/components/board");
const plugins_1 = require("~/plugins");
const players_1 = require("~/store/game/players");
const board_2 = require("@creature-chess/board");
const BoardSpaceFiller_1 = require("./BoardSpaceFiller");
const ThemedBoard_1 = require("./ThemedBoard");
const createClickEvent = (location) => ({ location });
const createDropPieceEvent = (id, location) => ({ id, location });
function useEvents({ onClick, onDropPiece, }) {
    const onClickBoard = React.useCallback(({ x, y }) => {
        if (!onClick) {
            return;
        }
        onClick(createClickEvent({ locationType: "board", x, y }));
    }, [onClick]);
    const onClickBench = React.useCallback(({ x }) => {
        if (!onClick) {
            return;
        }
        onClick(createClickEvent({ locationType: "bench", x }));
    }, [onClick]);
    const onDropBoard = React.useCallback(({ id, x, y }) => {
        if (!onDropPiece) {
            return;
        }
        onDropPiece(createDropPieceEvent(id, { locationType: "board", x, y }));
    }, [onDropPiece]);
    const onDropBench = React.useCallback(({ id, x }) => {
        if (!onDropPiece) {
            return;
        }
        onDropPiece(createDropPieceEvent(id, { locationType: "bench", x }));
    }, [onDropPiece]);
    return {
        onClickBoard,
        onClickBench,
        onDropBoard,
        onDropBench,
    };
}
function useRenderers(pieceRegistry, renderBoardPiece, renderBenchPiece, boardDraggable, benchDraggable) {
    const boardPieceRenderer = React.useMemo(() => (item, x, y) => {
        const piece = pieceRegistry.getPieceById(item);
        return {
            item: (React.createElement(plugins_1.Region, { cls: "player-piece-tile", id: `board.${x}.${y}`, ctx: {
                    pieceId: piece.id,
                    ownerId: piece.ownerId,
                    definitionId: piece.definitionId,
                    stage: piece.stage,
                    location: { type: "board", location: (0, board_2.packPosition)(x, y) },
                } }, renderBoardPiece(piece))),
            draggable: boardDraggable,
        };
    }, [boardDraggable, renderBoardPiece, pieceRegistry]);
    const benchPieceRenderer = React.useMemo(() => (item, x, _y) => {
        const piece = pieceRegistry.getPieceById(item);
        return {
            item: (React.createElement(plugins_1.Region, { cls: "player-piece-tile", id: `bench.${x}`, ctx: {
                    pieceId: piece.id,
                    ownerId: piece.ownerId,
                    definitionId: piece.definitionId,
                    stage: piece.stage,
                    location: { type: "bench", location: (0, board_2.packPosition)(x, 0) },
                } }, renderBenchPiece(piece))),
            draggable: benchDraggable,
        };
    }, [benchDraggable, renderBenchPiece, pieceRegistry]);
    return { boardPieceRenderer, benchPieceRenderer };
}
const useStyles = (0, react_jss_1.createUseStyles)({
    root: {
        height: "100%",
        width: "auto",
        containerName: "game-board",
        containerType: "size",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        flexDirection: "column",
    },
    gameBoard: ({ width, totalHeight }) => ({
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        overflow: "hidden",
        boxSizing: "border-box",
        flex: 1,
        // portrait
        [`@container game-board (max-aspect-ratio: ${width} / ${totalHeight})`]: {
            width: "calc(100% - 16px)",
            height: "auto",
        },
        // landscape
        [`@container game-board (min-aspect-ratio: ${width} / ${totalHeight})`]: {
            width: "auto",
            height: "calc(100% - 16px)",
        },
        aspectRatio: `${width} / ${totalHeight}`,
    }),
    board: {
        position: "relative",
        aspectRatio: ({ width, boardHalfHeight }) => `${width} / ${boardHalfHeight}`,
    },
    bench: {
        aspectRatio: ({ width }) => `${width} / 1`,
    },
    benchBoard: {
        display: "flex",
        flexDirection: "column",
        justifyContent: "end",
    },
    benchTile: {
        background: "#9e9e9e !important",
        boxShadow: "inset 0 0 2px #404040",
    },
});
function GameBoard({ board, bench, pieceRegistry, renderBoardPiece, renderBenchPiece, renderTileBackground, onClick, onDropPiece, boardDraggable, benchDraggable, children, showFiller = false, }) {
    var _a, _b;
    const { boardPieceRenderer, benchPieceRenderer } = useRenderers(pieceRegistry, renderBoardPiece, renderBenchPiece, boardDraggable, benchDraggable);
    const { onClickBoard, onClickBench, onDropBoard, onDropBench } = useEvents({
        onClick,
        onDropPiece,
    });
    const level = (_b = (_a = (0, players_1.useLocalPlayer)()) === null || _a === void 0 ? void 0 : _a.level) !== null && _b !== void 0 ? _b : 0;
    const canDropOnBoard = React.useCallback((id, x, y) => {
        const occupant = board.getPieceIdAtPosition(x, y);
        if (occupant === id) {
            return false;
        }
        if (occupant !== null) {
            // swap - no net piece count change
            return true;
        }
        if (board.containsPiece(id)) {
            return true;
        }
        return board.getAllPieces().length < level;
    }, [board, level]);
    const canDropOnBench = React.useCallback((id, x) => bench.getPieceIdAtPosition(x, 0) !== id, [bench]);
    const totalHeight = bench.height + (showFiller ? board.height * 2 : board.height);
    const styles = useStyles({
        boardHalfHeight: board.height,
        width: board.width,
        totalHeight,
    });
    const ref = React.useRef(null);
    return (React.createElement("div", { className: styles.root, ref: ref },
        React.createElement("div", { className: styles.gameBoard },
            showFiller && (React.createElement(BoardSpaceFiller_1.BoardSpaceFiller, { width: board.width, height: board.height })),
            React.createElement("div", { className: styles.board },
                React.createElement(ThemedBoard_1.ThemedBoard, { state: board, onDropItem: onDropBoard, onClickTile: onClickBoard, canDropItem: canDropOnBoard, renderItem: boardPieceRenderer, renderTileBackground: renderTileBackground, flipDarkLight: showFiller }),
                children),
            React.createElement("div", { className: styles.bench },
                React.createElement(board_1.BoardGrid, { state: bench, onDropItem: onDropBench, onClickTile: onClickBench, canDropItem: (id, x) => canDropOnBench(id, x), renderItem: benchPieceRenderer, className: styles.benchBoard, lightTileClassName: styles.benchTile, darkTileClassName: styles.benchTile })))));
}
